import { getUserErrorMessage } from '@packages/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';

declare global {
  interface Window {
    Tokenizer: new (
      url: string,
      containerId: string,
      options?: Record<string, unknown>,
    ) => {
      initialize: () => Promise<void>;
      submitTokenization: (options?: {
        cardholderName?: string;
        storePermanently?: boolean;
      }) => Promise<{
        success: boolean;
        hostedTokenizationId?: string;
        error?: { message?: string };
      }>;
      destroy: () => void;
    };
  }
}

export type WorldlineCustomerDevice = {
  acceptHeader: string;
  browserData: {
    colorDepth: number;
    javaEnabled: boolean;
    javaScriptEnabled: boolean;
    screenHeight: string;
    screenWidth: string;
  };
  ipAddress?: string;
  locale: string;
  timezoneOffsetUtcMinutes: string;
  userAgent: string;
};

export type WorldlineTokenizationSession = {
  hostedTokenizationUrl: string;
};

export type WorldlineCreatePaymentRequest = {
  hostedTokenizationId: string;
  customerDevice: WorldlineCustomerDevice;
};

export type WorldlineCreatePaymentResult =
  | { status: 'SUCCESS' }
  | {
      status: 'REDIRECT';
      paymentGatewayPaymentId: string;
      redirectUrl: string;
    }
  | {
      status: 'FAILED';
      errorMessage: string;
    };

const WORLDLINE_ACCEPT_HEADER =
  'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8';

const loadTokenizerScript = (tokenizerUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.Tokenizer) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${tokenizerUrl}"]`,
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load payment form')),
        {
          once: true,
        },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = tokenizerUrl;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load payment form'));
    document.head.appendChild(script);
  });
};

const getPublicIpAddress = async (): Promise<string | null> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');

    if (!response.ok) return null;

    const body = (await response.json()) as { ip?: unknown };
    return typeof body.ip === 'string' ? body.ip : null;
  } catch {
    return null;
  }
};

export const getWorldlineCustomerDevice = async (): Promise<WorldlineCustomerDevice> => ({
  acceptHeader: WORLDLINE_ACCEPT_HEADER,
  browserData: {
    colorDepth: window.screen.colorDepth,
    javaEnabled: navigator.javaEnabled?.() ?? false,
    javaScriptEnabled: true,
    screenHeight: String(window.screen.height),
    screenWidth: String(window.screen.width),
  },
  ipAddress: (await getPublicIpAddress()) ?? undefined,
  locale: navigator.language || 'en-AU',
  timezoneOffsetUtcMinutes: String(new Date().getTimezoneOffset()),
  userAgent: navigator.userAgent,
});

export const WorldlinePaymentForm = ({
  tokenizerUrl,
  submitLabel,
  createTokenizationSession,
  createPayment,
  onSuccess,
  onRedirect,
  onError,
  onSubmitting,
}: {
  tokenizerUrl: string;
  submitLabel: string;
  createTokenizationSession: () => Promise<WorldlineTokenizationSession>;
  createPayment: (args: WorldlineCreatePaymentRequest) => Promise<WorldlineCreatePaymentResult>;
  onSuccess: () => void;
  onRedirect: (result: Extract<WorldlineCreatePaymentResult, { status: 'REDIRECT' }>) => void;
  onError: (message: string) => void;
  onSubmitting?: () => void;
}) => {
  const iframeContainerId = useId().replace(/:/g, '_');
  const tokenizerRef = useRef<InstanceType<typeof window.Tokenizer> | null>(null);
  const [isLoadingForm, setIsLoadingForm] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const initializeForm = async () => {
      try {
        setIsLoadingForm(true);
        await loadTokenizerScript(tokenizerUrl);

        const tokenizationSession = await createTokenizationSession();

        if (isCancelled) return;

        const tokenizer = new window.Tokenizer(
          tokenizationSession.hostedTokenizationUrl,
          iframeContainerId,
          {
            hideCardholderName: false,
          },
        );

        await tokenizer.initialize();

        if (isCancelled) {
          tokenizer.destroy();
          return;
        }

        tokenizerRef.current = tokenizer;
        setIsLoadingForm(false);
      } catch (error) {
        if (isCancelled) return;
        onError(getUserErrorMessage(error));
        setIsLoadingForm(false);
      }
    };

    void initializeForm();

    return () => {
      isCancelled = true;

      if (tokenizerRef.current) {
        try {
          tokenizerRef.current.destroy();
        } catch {
          // ignore
        }
        tokenizerRef.current = null;
      }
    };
  }, [createTokenizationSession, iframeContainerId, onError, tokenizerUrl]);

  const handleSubmit = async () => {
    if (!tokenizerRef.current) return;

    setIsSubmitting(true);

    try {
      const tokenizationResult = await tokenizerRef.current.submitTokenization();

      // Card details failed the tokenizer's own validation — the iframe highlights the
      // offending fields, so keep the form on screen and let them fix it in place.
      if (!tokenizationResult.success || !tokenizationResult.hostedTokenizationId) {
        setIsSubmitting(false);
        return;
      }

      onSubmitting?.();

      const customerDevice = await getWorldlineCustomerDevice();

      const result = await createPayment({
        hostedTokenizationId: tokenizationResult.hostedTokenizationId,
        customerDevice,
      });

      if (result.status === 'SUCCESS') {
        onSuccess();
        return;
      }

      if (result.status === 'REDIRECT') {
        onRedirect(result);
        return;
      }

      onError(result.errorMessage);
      setIsSubmitting(false);
    } catch (error) {
      onError(getUserErrorMessage(error));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative min-h-[380px]">
        {isLoadingForm && (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingIcon />
          </div>
        )}
        <div
          id={iframeContainerId}
          className={`-mx-[20px] -mt-[20px] [&_iframe]:!w-full [&_iframe]:!border-none ${isLoadingForm ? 'invisible' : ''}`}
        />
      </div>

      <ActionButton
        className="w-full"
        size="lg"
        loading={isSubmitting}
        disabled={isLoadingForm || isSubmitting}
        onClick={() => {
          void handleSubmit();
        }}
      >
        {submitLabel}
      </ActionButton>
    </div>
  );
};
